/**
 * PageHead — declarative <title> + meta tag updater (no extra deps).
 *
 *   <PageHead
 *     title="Cart — ProvisionMart"
 *     description="…"
 *     image="/og/cart.png"
 *     jsonLd={{ "@context": "https://schema.org", "@type": "Product", … }}
 *   />
 *
 * We don't pull react-helmet because it adds ~17 KB; this hook-based approach
 * sets document.title + a small set of meta tags imperatively. SEO crawlers
 * that execute JS (Googlebot 2024+) read this fine; CRA SSG users will need
 * a static fallback in index.html (which we already provide).
 */
import React, { useEffect } from 'react';

export interface PageHeadProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  /** JSON-LD structured data injected as <script type="application/ld+json">. */
  jsonLd?: Record<string, any> | Array<Record<string, any>>;
  /** Stop the rest of the app from indexing this page. */
  noIndex?: boolean;
}

const upsertMeta = (selector: string, attrs: Record<string, string>) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    document.head.appendChild(el);
  }
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
};

const upsertJsonLd = (id: string, data: object | object[]) => {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.text = JSON.stringify(data);
};

const PageHead: React.FC<PageHeadProps> = ({ title, description, image, url, type = 'website', jsonLd, noIndex }) => {
  useEffect(() => {
    const original = document.title;
    if (title) document.title = title;

    if (description) {
      upsertMeta('meta[name="description"]', { name: 'description', content: description });
      upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    }
    if (title) {
      upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title });
      upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    }
    if (image) {
      upsertMeta('meta[property="og:image"]', { property: 'og:image', content: image });
      upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: image });
    }
    if (url) upsertMeta('meta[property="og:url"]', { property: 'og:url', content: url });
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: type });
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });

    if (noIndex) {
      upsertMeta('meta[name="robots"]', { name: 'robots', content: 'noindex,nofollow' });
    } else {
      upsertMeta('meta[name="robots"]', { name: 'robots', content: 'index,follow' });
    }

    if (jsonLd) upsertJsonLd('pm-jsonld', jsonLd);

    return () => {
      document.title = original;
      // Leave meta tags in place — the next route will overwrite them.
    };
  }, [title, description, image, url, type, jsonLd, noIndex]);

  return null;
};

export default PageHead;

